import { isValidCpfDigits } from '../crud/consulta_ws/helpers.js'

/**
 * Wizard de navegação por etapas para o formulário de admissão.
 *
 * Comportamento:
 * - Navegação sempre livre (Próximo, Anterior, clique na barra de progresso)
 * - Ao chegar em qualquer etapa: mostra alerta com campos pendentes (informativo)
 * - Alerta vai sumindo conforme o usuário preenche os campos (via input/change)
 * - Etapa ativa = azul; com pendência = amarela; completa = verde; não visitada = cinza
 * - Validação bloqueante apenas no botão "Enviar para aprovação"
 *
 * @param {HTMLElement} rootEl  Elemento raiz do controller admissoes--form
 */
export function initWizard(rootEl) {
  const allSections = Array.from(rootEl.querySelectorAll('[data-wizard-step]'))
  if (!allSections.length) return null

  const stepNums    = [...new Set(allSections.map(s => +s.dataset.wizardStep))].sort((a, b) => a - b)
  const total       = stepNums.length
  let current       = 0
  const visitedSteps = new Set()

  // Referências aos elementos de navegação
  const prevBtn   = rootEl.querySelector('[data-wizard-nav="prev"]')
  const nextBtn   = rootEl.querySelector('[data-wizard-nav="next"]')
  const enviarBtn = rootEl.querySelector('[data-wizard-nav="enviar"]')
  const stepBtns  = Array.from(rootEl.querySelectorAll('[data-wizard-nav="step"]'))
  const alertEl   = rootEl.querySelector('[data-wizard-alert]')
  const labelEl   = rootEl.querySelector('[data-wizard-nav="label"]')

  // ── helpers ────────────────────────────────────────────────────

  function sectionsAt(n) {
    return allSections.filter(s => +s.dataset.wizardStep === stepNums[n])
  }

  function esc(s) {
    return String(s || '')
      .replaceAll('&', '&amp;').replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;').replaceAll('"', '&quot;')
  }

  function fieldLabel(el, container) {
    if ((el.type || '') === 'file') {
      const uploadEl = el.closest('[data-controller~="crud--upload-arquivo"]')
      const lbl = uploadEl?.querySelector('label')
      if (lbl) return lbl.textContent.replace(/\*/g, '').trim()
    }
    // Para radio buttons, busca o label do grupo (sem atributo `for`) no wrapper .mb-4
    if ((el.type || '') === 'radio') {
      const wrapper = el.closest('.mb-4')
      if (wrapper) {
        const groupLabel = wrapper.querySelector('label:not([for])')
        if (groupLabel) return groupLabel.textContent.replace(/\*/g, '').trim()
      }
    }
    if (el.id) {
      const lbl = container.querySelector(`label[for="${CSS.escape(el.id)}"]`)
      if (lbl) return lbl.textContent.replace(/\*/g, '').trim()
    }
    return el.placeholder || el.name || 'Campo obrigatório'
  }

  function markTarget(el) {
    if ((el.type || '') === 'file') {
      return el.closest('[data-controller~="crud--upload-arquivo"]')
               ?.querySelector('.rounded-lg') || el
    }
    return el
  }

  function scrollTarget(el) {
    if ((el.type || '') === 'file') {
      return el.closest('[data-controller~="crud--upload-arquivo"]') || el.closest('.mb-4') || el
    }
    return el
  }

  // ── invalid field marks ────────────────────────────────────────
  let _markedEls = []

  function clearInvalidMarks() {
    _markedEls.forEach(({ el, outline, outlineOffset }) => {
      el.style.outline      = outline
      el.style.outlineOffset = outlineOffset
    })
    _markedEls = []
  }

  function markInvalid(els) {
    els.forEach(el => {
      const t = markTarget(el)
      _markedEls.push({ el: t, outline: t.style.outline || '', outlineOffset: t.style.outlineOffset || '' })
      t.style.outline       = '2px solid #ef4444'
      t.style.outlineOffset = '2px'
    })
  }

  /**
   * Verifica se o campo está visível e habilitado dentro do container.
   */
  function isVisible(el, container) {
    if (el.disabled) return false
    let node = el.parentElement
    while (node && node !== container) {
      if (node.dataset.crudAccordionTarget) {
        node = node.parentElement
        continue
      }
      if (node.style?.display === 'none' || node.classList?.contains('hidden')) return false
      node = node.parentElement
    }
    return true
  }

  /**
   * Valida os campos required da etapa n.
   * Retorna { errors: string[], badEls: HTMLElement[] }
   * silent=true: sem marcação visual nem scroll
   */
  function validateStep(n, { silent = false } = {}) {
    const errors  = []
    const badEls  = []
    let firstBad  = null
    const seen    = new Set()

    sectionsAt(n).forEach(sec => {
      sec.querySelectorAll(
        'input[required]:not([disabled]), select[required]:not([disabled]), textarea[required]:not([disabled])'
      ).forEach(el => {
        if (!isVisible(el, sec)) return

        const type = (el.type || '').toLowerCase()
        if (type === 'hidden') return

        if (type === 'file') {
          const widget = el.closest('[data-controller~="crud--upload-arquivo"]')
          if (widget && widget.querySelector('a[target="_blank"]')) return
          if (el.files && el.files.length > 0) return
          errors.push(fieldLabel(el, sec))
          badEls.push(el)
          firstBad ||= el
          return
        }

        if (type === 'radio') {
          const name = el.name
          if (!name || seen.has(name)) return
          seen.add(name)
          const group = [...sec.querySelectorAll(`input[type="radio"][name="${CSS.escape(name)}"]`)]
          if (!group.some(r => r.checked)) {
            errors.push(fieldLabel(el, sec))
            badEls.push(el)
            firstBad ||= el
          }
          return
        }

        if (!(el.value || '').trim()) {
          errors.push(fieldLabel(el, sec))
          badEls.push(el)
          firstBad ||= el
        }
      })

      sec.querySelectorAll('input[data-cpf-validate="true"]:not([disabled])').forEach(el => {
        if (!isVisible(el, sec)) return
        if (badEls.includes(el)) return
        const digits = (el.value || '').replace(/\D/g, '')
        if (digits && !isValidCpfDigits(digits)) {
          errors.push('CPF inválido')
          badEls.push(el)
          firstBad ||= el
        }
      })
    })

    if (!silent) {
      markInvalid(badEls)

      if (firstBad) {
        const st = scrollTarget(firstBad)
        requestAnimationFrame(() => {
          const panel = firstBad.closest('[data-crud--accordion-target="panel"]')
          if (panel?.classList.contains('hidden')) {
            const wrapper = panel.closest('[data-controller="crud--accordion"]')
            wrapper?.querySelector('[data-crud--accordion-target="header"]')?.click()
          }
          st.scrollIntoView({ behavior: 'smooth', block: 'center' })
          if (firstBad.type !== 'file') {
            try { firstBad.focus() } catch (_) {}
          }
        })
      }
    }

    return { errors, badEls }
  }

  // ── alert ──────────────────────────────────────────────────────

  function showAlert(errors, { scrollIntoView = false } = {}) {
    if (!alertEl) return
    alertEl.innerHTML = `
      <div class="rounded-lg ring-1 ring-red-200 bg-red-50 overflow-hidden">
        <div class="bg-red-500 text-white font-bold px-4 py-2 text-sm">
          Campos pendentes nesta etapa
        </div>
        <ul class="px-4 py-3 text-red-700 text-sm list-disc pl-8 space-y-1">
          ${errors.map(e => `<li>${esc(e)}</li>`).join('')}
        </ul>
      </div>`
    if (scrollIntoView) alertEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  function hideAlert() {
    if (alertEl) alertEl.innerHTML = ''
  }

  // ── validação informativa do step atual ────────────────────────
  // Chamada ao chegar numa etapa E a cada campo alterado (input/change).
  // Nunca bloqueia — apenas informa.
  function refreshAlert() {
    clearInvalidMarks()
    const { errors, badEls } = validateStep(current, { silent: true })
    if (errors.length) {
      markInvalid(badEls)
      showAlert(errors)
    } else {
      hideAlert()
    }
  }

  // ── render ─────────────────────────────────────────────────────

  function updateUI() {
    // Mostrar/ocultar seções por etapa
    stepNums.forEach((_, i) => {
      sectionsAt(i).forEach(sec => sec.classList.toggle('hidden', i !== current))
    })

    // Botões de etapa (barra de progresso)
    stepBtns.forEach((btn, i) => {
      const active = i === current
      btn.classList.toggle('bg-blue-600',       active)
      btn.classList.toggle('text-white',         active)
      btn.classList.toggle('bg-gray-100',        !active)
      btn.classList.toggle('text-gray-600',      !active)
      btn.classList.toggle('hover:bg-gray-200',  !active)
      // Remove estados do section_progress ao ativar — evita conflito de bg-* no CSS
      if (active) btn.classList.remove('bg-green-600', 'bg-yellow-400', 'text-gray-900')

      // Círculo numerado
      const circle = btn.querySelector('[data-wizard-circle]')
      if (circle) {
        circle.classList.remove(
          'border-white', 'text-white',
          'border-gray-400', 'text-gray-500',
          'border-green-200', 'border-yellow-200', 'text-gray-900',
          'bg-white', 'bg-green-700', 'bg-yellow-500', 'text-blue-600'
        )
        if (active) {
          circle.classList.add('border-white', 'text-white')
        }
        // se inativo: section_progress aplica o estado correto via refreshAll
      }
    })

    // Anterior: invisível na etapa 0
    if (prevBtn) prevBtn.classList.toggle('invisible', current === 0)

    // Próximo ↔ Enviar
    const isLast = current === total - 1
    if (nextBtn)   nextBtn.classList.toggle('hidden', isLast)
    if (enviarBtn) enviarBtn.classList.toggle('hidden', !isLast)

    // Label "Etapa N de M"
    if (labelEl) labelEl.textContent = `Etapa ${current + 1} de ${total}`
  }

  function goTo(n) {
    // Marca o step que estamos SAINDO como visitado
    visitedSteps.add(current)
    current = n
    updateUI()
    rootEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
    // Mostra alerta apenas em steps já visitados; na primeira passada, limpa marcas
    if (visitedSteps.has(n)) {
      refreshAlert()
    } else {
      clearInvalidMarks()
      hideAlert()
    }
  }

  // ── listeners ──────────────────────────────────────────────────

  nextBtn?.addEventListener('click', () => {
    if (current < total - 1) goTo(current + 1)
  })

  prevBtn?.addEventListener('click', () => {
    if (current > 0) goTo(current - 1)
  })

  // Clique direto nos botões de etapa: navegação sempre livre.
  stepBtns.forEach((btn, i) => {
    btn.addEventListener('click', () => {
      if (i === current) return
      goTo(i)
    })
  })

  // Atualiza alerta ao editar campos (apenas em steps já visitados)
  const formEl = rootEl.querySelector('form')
  formEl?.addEventListener('input',  () => { if (visitedSteps.has(current)) refreshAlert() })
  formEl?.addEventListener('change', () => { if (visitedSteps.has(current)) refreshAlert() })

  // ── Validação completa ao clicar em Enviar ──────────────────────
  formEl?.addEventListener('submit', (ev) => {
    if (!enviarBtn || ev.submitter !== enviarBtn) return

    clearInvalidMarks()
    const allErrors = []
    let firstFailingStep = -1

    for (let n = 0; n < total; n++) {
      const { errors } = validateStep(n, { silent: true })
      if (errors.length) {
        if (firstFailingStep === -1) firstFailingStep = n
        allErrors.push(...errors)
      }
    }

    if (!allErrors.length) return

    ev.preventDefault()
    ev.stopImmediatePropagation()
    if (firstFailingStep !== -1) {
      current = firstFailingStep
      updateUI()
      const { badEls } = validateStep(firstFailingStep, { silent: true })
      markInvalid(badEls)
    }
    showAlert(allErrors, { scrollIntoView: true })
  }, true)

  updateUI()

  // Em modo edição, avança direto para a primeira etapa com campos incompletos
  const isEditMode = !!rootEl.querySelector('form input[name="_method"]')
  if (isEditMode) {
    for (let n = 0; n < total; n++) {
      const { errors } = validateStep(n, { silent: true })
      if (errors.length) {
        current = n
        visitedSteps.add(n)
        updateUI()
        refreshAlert()
        break
      }
    }
  }

  function navigateToFirstIncomplete() {
    for (let n = 0; n < total; n++) {
      const { errors } = validateStep(n, { silent: true })
      if (errors.length) {
        current = n
        updateUI()
        if (isEditMode) refreshAlert()
        return
      }
    }
    current = 0
    updateUI()
  }

  return { goTo, getStep: () => current, navigateToFirstIncomplete }
}
